import React, { Suspense, lazy } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import PartnerAuth0Provider from './components/PartnerAuth0Provider';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
// PWA Install Prompt disabled - user feedback indicated it was obstructive
// import PWAInstallPrompt from './components/PWAInstallPrompt';
import MobileBanner from './components/MobileBanner';
import SEO from './components/SEO';
import GEOContent from './components/GEOContent';
import MaintenanceWrapper from './components/MaintenanceWrapper';
import ScrollToTop from './components/ScrollToTop';
import LoadingSpinner from './components/LoadingSpinner';
import { NotificationProvider } from './contexts/NotificationContext';
import { CookieConsentProvider } from './contexts/CookieConsentContext';
import CookieConsentBanner from './components/CookieConsentBanner';
import CookieManagementModal from './components/CookieManagementModal';
import WhiteLabelProvider from './components/WhiteLabelProvider';
import DemoControlPanel from './components/DemoControlPanel';
import GlobalErrorBoundary from './components/GlobalErrorBoundary';
// import CookieDevPanel from './components/CookieDevPanel';
// import { SentryErrorBoundary, setSentryUser, clearSentryUser } from './sentry';

// Page imports
import Home from './pages/Home';
import About from './pages/About';
import Contact from './pages/Contact';
import Login from './pages/Login';
import SignUp from './pages/SignUp';
import ForgotPassword from './pages/ForgotPassword';
import VerifyEmail from './pages/VerifyEmail';
import EmailChangeApproved from './pages/EmailChangeApproved';
import EmailChangeRejected from './pages/EmailChangeRejected';
import CpAcknowledged from './pages/CpAcknowledged';
import CreateFamily from './pages/CreateFamily';
import Dashboard from './pages/Dashboard';
import MemberSelection from './pages/MemberSelection';
import PasscodeEntry from './pages/PasscodeEntry';
import MemberLogin from './pages/MemberLogin';
import AddMember from './pages/AddMember';
// Lazy load heavy components for better performance
const FamilyTree = lazy(() => import('./pages/FamilyTree'));
const Gallery = lazy(() => import('./pages/Gallery'));
const PrivacyFolder = lazy(() => import('./pages/PrivacyFolder'));
const UploadMemory = lazy(() => import('./pages/UploadMemory'));
const EmergencyDashboard = lazy(() => import('./pages/EmergencyDashboard'));
const EmergencyAccessPage = lazy(() => import('./pages/EmergencyAccessPage'));
const EmergencyAccess = lazy(() => import('./pages/EmergencyAccess'));
const ChoosePlan = lazy(() => import('./pages/ChoosePlan'));
const AccountManagement = lazy(() => import('./pages/AccountManagement'));
const AuditLogs = lazy(() => import('./pages/AuditLogs'));
const AuditTrail = lazy(() => import('./pages/AuditTrail'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const ThankYou = lazy(() => import('./pages/ThankYou'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const HowItWorks = lazy(() => import('./pages/HowItWorks'));
const LegacyCaseStudies = lazy(() => import('./pages/LegacyCaseStudies'));
const Legal = lazy(() => import('./pages/Legal'));
const DirectSignup = lazy(() => import('./pages/DirectSignup'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const ForgotPasscode = lazy(() => import('./pages/ForgotPasscode'));
const ResetPasscode = lazy(() => import('./components/ResetPasscode'));
const ResetSuccess = lazy(() => import('./pages/ResetSuccess'));
const MessageCenter = lazy(() => import('./components/MessageCenter'));
const SecurityDemo = lazy(() => import('./pages/SecurityDemo'));
const MFATest = lazy(() => import('./pages/MFATest'));
const EncryptionTest = lazy(() => import('./pages/EncryptionTest'));
const TestEmergencyAccess = lazy(() => import('./pages/TestEmergencyAccess'));
const EmergencyAccessDemo = lazy(() => import('./pages/EmergencyAccessDemo'));
const Security = lazy(() => import('./pages/Security'));
const SecurityTransparency = lazy(() => import('./pages/SecurityTransparency'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const CompetitorComparison = lazy(() => import('./pages/CompetitorComparison'));
const EnhancedPrivacyFolders = lazy(() => import('./pages/EnhancedPrivacyFolders'));
const PressMedia = lazy(() => import('./pages/PressMedia'));
const AIOptimizedPage = lazy(() => import('./pages/AIOptimized'));
const Demo = lazy(() => import('./pages/Demo'));
const FamilyVault = lazy(() => import('./pages/FamilyVault/index.jsx'));
const ContinuityTimeline = lazy(() => import('./pages/ContinuityTimeline'));
const FamilyStories = lazy(() => import('./pages/FamilyStories'));
const OfflineContinuity = lazy(() => import('./pages/OfflineContinuity/index.jsx'));
const OfflineContinuityFamilyView = lazy(() => import('./pages/OfflineContinuity/FamilyView.jsx'));
const GovernanceRequests = lazy(() => import('./pages/GovernanceRequests/index.jsx'));
const LegacyJournalHome = lazy(() => import('./pages/LegacyJournalHome'));
const LegacyArticle = lazy(() => import('./pages/LegacyArticle'));
const ArchiveHome = lazy(() => import('./pages/ArchiveHome'));
const ArchiveEntry = lazy(() => import('./pages/ArchiveEntry'));
const FounderMusings = lazy(() => import('./pages/FounderMusings'));

// Landing Pages
const DigitalFamilyVault = lazy(() => import('./pages/LandingPages/DigitalFamilyVault'));
const LegacyPlanning = lazy(() => import('./pages/LandingPages/LegacyPlanning'));
const EstateDocumentStorage = lazy(() => import('./pages/LandingPages/EstateDocumentStorage'));
const DigitalVault = lazy(() => import('./pages/LandingPages/DigitalVault'));

// Blog
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/Blog/BlogPost'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const ExploreFeatures = lazy(() => import('./pages/ExploreFeatures'));
const FamilyLegacyGuide = lazy(() => import('./pages/FamilyLegacyGuide'));
const WhyEternaVaults = lazy(() => import('./pages/WhyEternaVaults'));
const PillarsOfContinuity = lazy(() => import('./pages/PillarsOfContinuity'));
const ContinuityArchitecture = lazy(() => import('./pages/ContinuityArchitecture'));
const ContinuityArchitectureStack = lazy(() => import('./pages/ContinuityArchitectureStack'));
const AuthorityAndDecisionArchitecture = lazy(() => import('./pages/AuthorityAndDecisionArchitecture'));
const Glossary = lazy(() => import('./pages/Glossary'));
const TemporalContinuity = lazy(() => import('./pages/TemporalContinuity'));
const ContinuityInfrastructure = lazy(() => import('./pages/ContinuityInfrastructure'));
const ContinuitySeismicMap = lazy(() => import('./pages/ContinuitySeismicMap'));
const GovernanceVsStorage = lazy(() => import('./pages/GovernanceVsStorage'));
const WhatWeAreKnownFor = lazy(() => import('./pages/WhatWeAreKnownFor'));
const TheProblem = lazy(() => import('./pages/TheProblem'));
const ContinuityInfrastructureReview = lazy(() => import('./pages/ContinuityInfrastructureReview'));
const TemporalContinuityExecution = lazy(() => import('./pages/TemporalContinuityExecution'));
const SuccessionAwareArchitecture = lazy(() => import('./pages/SuccessionAwareArchitecture'));
const PrivateDigitalHeirlooms = lazy(() => import('./pages/PrivateDigitalHeirlooms'));
const ContinuityOS = lazy(() => import('./pages/ContinuityOS'));

// Brochures
const BrochurePasscodeGate = lazy(() => import('./components/BrochurePasscodeGate'));
const BrochuresIndex = lazy(() => import('./brochures/index'));
const C01_Breakage = lazy(() => import('./brochures/courses/C01_Breakage'));
const C02_Layer = lazy(() => import('./brochures/courses/C02_Layer'));
const C03_Architecture = lazy(() => import('./brochures/courses/C03_Architecture'));
const C04_Advisor = lazy(() => import('./brochures/courses/C04_Advisor'));
const C05_FamilyOffice = lazy(() => import('./brochures/courses/C05_FamilyOffice'));
const C06_Boundary = lazy(() => import('./brochures/courses/C06_Boundary'));
const C07_Commissioning = lazy(() => import('./brochures/courses/C07_Commissioning'));
const C07_FO = lazy(() => import('./brochures/courses/C07_FO'));
const C07_ADV = lazy(() => import('./brochures/courses/C07_ADV'));
const C08_Invitation = lazy(() => import('./brochures/courses/C08_Invitation'));
const C09_FoundingPartner = lazy(() => import('./brochures/courses/C09_FoundingPartner'));
const C10_CoBranded = lazy(() => import('./brochures/courses/C10_CoBranded'));
const C11_WhiteLabel = lazy(() => import('./brochures/courses/C11_WhiteLabel'));
const C12_FOEdition = lazy(() => import('./brochures/courses/C12_FOEdition'));
const C13_PrivateEdition = lazy(() => import('./brochures/courses/C13_PrivateEdition'));
const ZeroKnowledgeGovernance = lazy(() => import('./pages/ZeroKnowledgeGovernance'));
const ContinuityDomains = lazy(() => import('./pages/ContinuityDomains'));
const ContinuityInfrastructureArticle = lazy(() => import('./pages/ContinuityInfrastructureArticle'));
const TemporalContinuityEternaVaults = lazy(() => import('./pages/TemporalContinuityEternaVaults'));
const FirevaultColdStorageEternaVaults = lazy(() => import('./pages/FirevaultColdStorageEternaVaults'));
const SuccessionAwareArchitectureEternaVaults = lazy(() => import('./pages/SuccessionAwareArchitectureEternaVaults'));
const ProcessPage = lazy(() => import('./pages/ProcessPage'));
const FAQ = lazy(() => import('./pages/FAQ'));
const Membership = lazy(() => import('./pages/Pricing'));
const RateLimit = lazy(() => import('./pages/RateLimit'));
const WhoWeServe = lazy(() => import('./pages/WhoWeServe'));
const WhoWeWorkWith = lazy(() => import('./pages/WhoWeWorkWith'));
const EmergencyAccessModel = lazy(() => import('./pages/EmergencyAccessModel'));
const AuthorityActivationFlow = lazy(() => import('./pages/AuthorityActivationFlow'));
const Reviews = lazy(() => import('./pages/Reviews'));
const NotFound = lazy(() => import('./pages/NotFound'));
const ZeroKnowledgeArchitecture = lazy(() => import('./pages/Blog/ZeroKnowledgeArchitecture'));
const DigitalInheritanceSecurity = lazy(() => import('./pages/Blog/DigitalInheritanceSecurity'));
const FamilyDataProtection = lazy(() => import('./pages/Blog/FamilyDataProtection'));
const EncryptionBestPractices = lazy(() => import('./pages/Blog/EncryptionBestPractices'));
const WhatIsDigitalVaultBlog = lazy(() => import('./pages/Blog/WhatIsDigitalVaultComprehensive'));
const DigitalLegacyPlanning2025 = lazy(() => import('./pages/Blog/DigitalLegacyPlanningCompleteGuide2025'));
const DigitalEstatePlanningGap2025 = lazy(() => import('./pages/Blog/DigitalEstatePlanningGap2025'));
const DigitalContinuityInfrastructure = lazy(() => import('./pages/Blog/DigitalContinuityInfrastructure'));
const DigitalContinuityForFamilyOffices = lazy(() => import('./pages/Blog/DigitalContinuityForFamilyOffices'));
const BestDigitalVault2025 = lazy(() => import('./pages/BestDigitalVault2025'));
const WhatIsDigitalVault = lazy(() => import('./pages/WhatIsDigitalVault'));
const DigitalVaultReviews = lazy(() => import('./pages/DigitalVaultReviews'));
// const EternaVaultsVsPresidio2025 = lazy(() => import('./pages/Blog/EternaVaultsVsPresidio2025'));
const DigitalVaultPage = lazy(() => import('./pages/DigitalVault'));
const WhyTraditionalCloudStorageFails = lazy(() => import('./pages/Blog/WhyTraditionalCloudStorageFailsForFamilyMemories'));
const EmergencyAccessProtectingDigitalAssets = lazy(() => import('./pages/Blog/EmergencyAccessProtectingDigitalAssets'));
const TheHiddenCostsOfLosingFamilyDigitalMemories = lazy(() => import('./pages/Blog/TheHiddenCostsOfLosingFamilyDigitalMemories'));
const DigitalVaultSecurity2024 = lazy(() => import('./pages/Blog/DigitalVaultSecurity2024'));
const DigitalVaultAccessLevels = lazy(() => import('./pages/Blog/DigitalVaultAccessLevels'));
const DigitalVaultSydney = lazy(() => import('./pages/LandingPages/DigitalVaultSydney'));
const DigitalVaultMelbourne = lazy(() => import('./pages/LandingPages/DigitalVaultMelbourne'));
const DigitalVaultBrisbane = lazy(() => import('./pages/LandingPages/DigitalVaultBrisbane'));
const FamilyOfficeDigitalLegacy = lazy(() => import('./pages/LandingPages/FamilyOfficeDigitalLegacy'));
const InstitutionalDigitalVault = lazy(() => import('./pages/InstitutionalDigitalVault'));
const AdvisorAccess = lazy(() => import('./pages/AdvisorAccess/AdvisorAccess'));
const WhiteLabel = lazy(() => import('./pages/WhiteLabel'));
const InvestorTeaser = lazy(() => import('./pages/InvestorTeaser'));
const AdvisorPack = lazy(() => import('./pages/AdvisorPack'));
const FOContinuityInfrastructure = lazy(() => import('./pages/FOContinuityInfrastructure'));
const LegalContinuityInfrastructure = lazy(() => import('./pages/LegalContinuityInfrastructure'));
const Roadmap = lazy(() => import('./pages/Roadmap'));
const VerificationResponse = lazy(() => import('./pages/VerificationResponse'));
const EvOutreach = lazy(() => import('./pages/EvOutreach'));
// Partner Portal
import PartnerLogin from './pages/PartnerLogin';
import PartnerDashboard from './pages/PartnerDashboard';
import PartnerInvite from './pages/PartnerInvite';
import PartnerProtectedRoute from './components/PartnerProtectedRoute';
import FOProtectedRoute from './components/FOProtectedRoute';
import EmergencyApproval from './pages/EmergencyApproval';
import EmergencyDualApproval from './pages/EmergencyDualApproval';
import FamilyApproval from './pages/FamilyApproval';
const LegalDocumentReview = lazy(() => import('./pages/LegalDocumentReview'));
const PartnerFamilyList = lazy(() => import('./pages/PartnerFamilyList'));
const PartnerCreateFamily = lazy(() => import('./pages/PartnerCreateFamily'));
const PartnerManageFamily = lazy(() => import('./pages/PartnerManageFamily'));
const PartnerEmergencyVault = lazy(() => import('./pages/PartnerEmergencyVault'));
const PartnerCommunications = lazy(() => import('./pages/PartnerCommunications'));
const PartnerReports = lazy(() => import('./pages/PartnerReports'));
const PartnerSuccessionTab = lazy(() => import('./pages/PartnerDashboard/SuccessionTab'));
const PartnerBranding = lazy(() => import('./pages/PartnerBranding'));
const PartnerAccountSettings = lazy(() => import('./pages/PartnerAccountSettings'));
const AdvisorPackView = lazy(() => import('./pages/AdvisorPackView'));
const FamilyOfficePack = lazy(() => import('./pages/FamilyOfficePack'));
const EngagementOutlinePack = lazy(() => import('./pages/EngagementOutlinePack'));
const PrivateEditionPack = lazy(() => import('./pages/PrivateEditionPack'));
const PatronsPack = lazy(() => import('./pages/PatronsPack'));
const FODashboard = lazy(() => import('./pages/FODashboard'));
const FOIdentityMap = lazy(() => import('./pages/FODashboard/IdentityMap'));
const FORiskGraph = lazy(() => import('./pages/FODashboard/RiskGraph'));
const FOCrossFamily = lazy(() => import('./pages/FODashboard/CrossFamily'));
const FOEmergencyGovernance = lazy(() => import('./pages/FODashboard/EmergencyGovernance'));
const FOEmergencyApprovals = lazy(() => import('./pages/FODashboard/EmergencyApprovals'));
const FOSuccession = lazy(() => import('./pages/FODashboard/SuccessionTab'));
const FOCommunications = lazy(() => import('./pages/FODashboard/Communications'));
const FOReports = lazy(() => import('./pages/FODashboard/Reports'));
const FOLedger = lazy(() => import('./pages/FODashboard/Ledger'));
const FOFamilies = lazy(() => import('./pages/FODashboard/Families'));
const FOCreateFamily = lazy(() => import('./pages/FODashboard/CreateFamily'));
const FOFamilyDetail = lazy(() => import('./pages/FODashboard/FamilyDetail'));
const FOFamilySuccessionTab = lazy(() => import('./pages/FODashboard/FOFamilySuccessionTab'));
const FOContinuityDependencies = lazy(() => import('./pages/FODashboard/ContinuityDependencies'));
const FOReviewSignals = lazy(() => import('./pages/FODashboard/QuietReviewSignals'));
const FOStabilityLens = lazy(() => import('./pages/FODashboard/StabilityLens'));
const FOSeismicDrift = lazy(() => import('./pages/FODashboard/SeismicDrift'));
const FOStabilityQuestionnaire = lazy(() => import('./pages/FODashboard/StabilityQuestionnaire'));
const FOEmergencyVault = lazy(() => import('./pages/FOEmergencyVault'));
const FODocumentUpload = lazy(() => import('./pages/FODocumentUpload'));
const FOAccountManagement = lazy(() => import('./pages/FOAccountManagement'));
const SecurityDashboard = lazy(() => import('./pages/SecurityDashboard'));
const PrivateEditions = lazy(() => import('./pages/PrivateEditions'));
const InstitutionalSolutions = lazy(() => import('./pages/InstitutionalSolutions'));
const ZeroKnowledgeTest = lazy(() => import('./pages/ZeroKnowledgeTest'));
const HowToGuides = lazy(() => import('./pages/HowToGuides'));
const DocumentSignaturePage = lazy(() => import('./pages/DocumentSignature'));
const AddonFeatures = lazy(() => import('./components/AddonFeatures'));
const Maintenance = lazy(() => import('./pages/Maintenance'));
const ContinuityActivationMandate = lazy(() => import('./pages/ContinuityActivationMandate'));
const Resources = lazy(() => import('./pages/Resources'));
const Monitoring = lazy(() => import('./components/SimpleMonitoring'));
const UXDemo = lazy(() => import('./pages/UXDemo'));
const CompareDigitalLegacyPlatforms = lazy(() => import('./pages/Blog/CompareDigitalLegacyPlatforms'));
const FamilyDigitalVaultAccessLevels = lazy(() => import('./pages/Blog/FamilyDigitalVaultAccessLevels'));
const PreservingFamilyStoriesDigitalLegacy = lazy(() => import('./pages/Blog/PreservingFamilyStoriesDigitalLegacy'));
const TheArchitectureOfLegacy = lazy(() => import('./pages/Blog/TheArchitectureOfLegacy'));
const TheNewHeirloom = lazy(() => import('./pages/Blog/TheNewHeirloom'));
const BusinessCard = lazy(() => import('./pages/BusinessCard'));
const AdvisorStewardshipAccess = lazy(() => import('./pages/AdvisorStewardshipAccess'));
const AdminPartnerManagement = lazy(() => import('./pages/AdminPartnerManagement'));
const AccessRequestConfirmation = lazy(() => import('./pages/AccessRequestConfirmation'));
const InviteSignup = lazy(() => import('./pages/InviteSignup'));
const AdminAccessRequests = lazy(() => import('./pages/AdminAccessRequests'));
const StyleGuide = lazy(() => import('./pages/StyleGuide'));
const Firevault = lazy(() => import('./pages/Firevault'));
const PendingApprovalsPage = lazy(() => import('./pages/PendingApprovalsPage'));
const Documentation = lazy(() => import('./pages/Documentation'));
const DemoHub = lazy(() => import('./pages/DemoHub'));
const InternalDemoScripts = lazy(() => import('./pages/InternalDemoScripts'));
const BookSession = lazy(() => import('./pages/BookSession'));
const ForAdvisors = lazy(() => import('./pages/ForAdvisors'));
const Commissioning = lazy(() => import('./pages/Commissioning'));

// Sovereign Aegis - Multi-Signature Recovery
const SovereignAegis = lazy(() => import('./pages/SovereignAegis'));
const RecoveryInitiate = lazy(() => import('./pages/RecoveryInitiate'));
const RecoveryStatus = lazy(() => import('./pages/RecoveryStatus'));
const GuardianApproval = lazy(() => import('./pages/GuardianApproval'));
const GuardianAccept = lazy(() => import('./pages/GuardianAccept'));
const RecoveryComplete = lazy(() => import('./pages/RecoveryComplete'));

// Succession Planning - Dedicated Page
const SuccessionPlanningPage = lazy(() => import('./pages/SuccessionPlanning'));

import { withAuthenticationRequired } from '@auth0/auth0-react';
import Navigation from './components/Navigation';
import SimplifiedNavigation from './components/SimplifiedNavigation';
import Footer from './components/Footer';
import SubscriptionGuard from './components/SubscriptionGuard';
import './App.css';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Wrap your protected component with both Auth0 and Subscription protection
const ProtectedDashboard = withAuthenticationRequired(
  () => <SubscriptionGuard><Dashboard /></SubscriptionGuard>,
  { onRedirecting: () => <div>Loading...</div> }
);

const ProtectedAuditLogs = withAuthenticationRequired(
  () => <SubscriptionGuard><AuditLogs /></SubscriptionGuard>,
  { onRedirecting: () => <div>Loading...</div> }
);

const ProtectedAuditTrail = withAuthenticationRequired(
  () => <SubscriptionGuard><AuditTrail /></SubscriptionGuard>,
  { onRedirecting: () => <div>Loading...</div> }
);

const ProtectedPendingApprovalsPage = withAuthenticationRequired(
  () => <SubscriptionGuard><PendingApprovalsPage /></SubscriptionGuard>,
  { onRedirecting: () => <div>Loading...</div> }
);

const ProtectedEmergencyAccess = withAuthenticationRequired(
  () => <SubscriptionGuard><EmergencyAccess /></SubscriptionGuard>,
  { onRedirecting: () => <div>Loading...</div> }
);

const ProtectedOnboarding = withAuthenticationRequired(
  () => <SubscriptionGuard><Onboarding /></SubscriptionGuard>,
  { onRedirecting: () => <div>Loading...</div> }
);

const ProtectedZeroKnowledgeTest = withAuthenticationRequired(
  () => <SubscriptionGuard><ZeroKnowledgeTest /></SubscriptionGuard>,
  { onRedirecting: () => <div>Loading...</div> }
);

const ProtectedEmergencyAccessPage = withAuthenticationRequired(
  () => <SubscriptionGuard><EmergencyAccessPage /></SubscriptionGuard>,
  { onRedirecting: () => <div>Loading...</div> }
);

const ProtectedMessageCenter = withAuthenticationRequired(
  () => <SubscriptionGuard><MessageCenter /></SubscriptionGuard>,
  { onRedirecting: () => <div>Loading...</div> }
);

const ProtectedGovernanceRequests = withAuthenticationRequired(
  () => <SubscriptionGuard><GovernanceRequests /></SubscriptionGuard>,
  { onRedirecting: () => <div>Loading...</div> }
);

const ProtectedFirevault = withAuthenticationRequired(
  () => <SubscriptionGuard><Firevault /></SubscriptionGuard>,
  { onRedirecting: () => <div>Loading...</div> }
);

const ProtectedAdminPartnerManagement = withAuthenticationRequired(
  () => <SubscriptionGuard><AdminPartnerManagement /></SubscriptionGuard>,
  { onRedirecting: () => <div>Loading...</div> }
);

const ProtectedAdminAccessRequests = withAuthenticationRequired(
  () => <SubscriptionGuard><AdminAccessRequests /></SubscriptionGuard>,
  { onRedirecting: () => <div>Loading...</div> }
);

// Components that need subscription protection but not Auth0 (for member access)
const SubscriptionProtectedFamilyTree = () => <SubscriptionGuard><FamilyTree /></SubscriptionGuard>;
const SubscriptionProtectedGallery = () => <SubscriptionGuard><Gallery /></SubscriptionGuard>;
const SubscriptionProtectedPrivacyFolder = () => <SubscriptionGuard><PrivacyFolder /></SubscriptionGuard>;
const SubscriptionProtectedEnhancedPrivacyFolders = () => <SubscriptionGuard><EnhancedPrivacyFolders /></SubscriptionGuard>;
const SubscriptionProtectedUploadMemory = () => <SubscriptionGuard><UploadMemory /></SubscriptionGuard>;
const SubscriptionProtectedAccountManagement = () => <SubscriptionGuard><AccountManagement /></SubscriptionGuard>;
const SubscriptionProtectedAddMember = () => <SubscriptionGuard><AddMember /></SubscriptionGuard>;
const SubscriptionProtectedMemberSelection = () => <SubscriptionGuard><MemberSelection /></SubscriptionGuard>;
const SubscriptionProtectedPasscodeEntry = () => <SubscriptionGuard><PasscodeEntry /></SubscriptionGuard>;
const SubscriptionProtectedMemberLogin = () => <SubscriptionGuard><MemberLogin /></SubscriptionGuard>;
// CreateFamily should be subscription protected - users need valid subscription after payment
const SubscriptionProtectedCreateFamily = () => <SubscriptionGuard><CreateFamily /></SubscriptionGuard>;

const SubscriptionProtectedFamilyVault = () => <SubscriptionGuard><FamilyVault /></SubscriptionGuard>;
const SubscriptionProtectedContinuityTimeline = () => <SubscriptionGuard><ContinuityTimeline /></SubscriptionGuard>;
const SubscriptionProtectedFamilyStories = () => <SubscriptionGuard><FamilyStories /></SubscriptionGuard>;
const SubscriptionProtectedSovereignAegis = () => <SubscriptionGuard><SovereignAegis /></SubscriptionGuard>;
const SubscriptionProtectedSuccessionPlanning = () => <SubscriptionGuard><SuccessionPlanningPage /></SubscriptionGuard>;
const ProtectedDemo = withAuthenticationRequired(Demo, {
  onRedirecting: () => <div>Loading...</div>
});
const ProtectedStyleGuide = withAuthenticationRequired(StyleGuide, {
  onRedirecting: () => <div>Loading...</div>
});

// Emergency dashboard should not require Auth0 authentication since it uses emergency tokens
// const ProtectedEmergencyDashboard = withAuthenticationRequired(EmergencyDashboard, {
//   onRedirecting: () => <div>Loading...</div>
// });

// Conditional Footer Component
function ConditionalFooter() {
  const location = useLocation();
  const { isAuthenticated } = useAuth0();
  const hideFooterPaths = []; // Paths where footer should be hidden (main page now shows footer)

  // Hide footer when logged in or on specific paths
  if (isAuthenticated || hideFooterPaths.includes(location.pathname)) {
    return null;
  }

  return <Footer />;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <GlobalErrorBoundary>
        <PartnerAuth0Provider>
          <WhiteLabelProvider>
            <NotificationProvider>
              <HelmetProvider>
                <SEO />
                <GEOContent />
                <MobileBanner />
                <Navigation />
                <ScrollToTop />
                <Suspense fallback={<LoadingSpinner type="page" message="Loading..." />}>
                  <MaintenanceWrapper>
                    <Routes>
                      <Route path="/auth/callback" element={<AuthCallback />} />
                      <Route path="/payment-success" element={<PaymentSuccess />} />
                      <Route path="/dashboard" element={<Navigate to="/member-selection" replace />} />
                      <Route path="/" element={<Home />} />
                      {(process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') && (
                        <>
                          <Route path="/documentation" element={<Documentation />} />
                          <Route path="/demo-hub" element={<DemoHub />} />
                          <Route path="/demo-scripts" element={<InternalDemoScripts />} />
                          <Route path="/roadmap" element={<Roadmap />} />
                          <Route path="/ev-outreach" element={<EvOutreach />} />
                          <Route path="/verify/:token" element={<VerificationResponse />} />
                        </>
                      )}
                      <Route path="/dashboard/:memberId" element={<ProtectedDashboard />} />
                      <Route path="/family-tree" element={<SubscriptionProtectedFamilyTree />} />
                      <Route path="/about" element={<About />} />
                      <Route path="/press" element={<PressMedia />} />
                      <Route path="/digital-legacy-info" element={<AIOptimizedPage />} />
                      <Route path="/faq" element={<FAQ />} />
                      <Route path="/membership" element={<Navigate to="/private-membership" replace />} />
                      <Route path="/private-membership" element={<Membership />} />
                      <Route path="/rate-limit" element={<RateLimit />} />
                      <Route path="/who-we-work-with" element={<WhoWeWorkWith />} />
                      <Route path="/who-we-serve" element={<WhoWeServe />} />
                      <Route path="/who-we-partner-with" element={<WhoWeServe />} />
                      <Route path="/emergency-access-model" element={<EmergencyAccessModel />} />
                      <Route path="/authority-activation-flow" element={<AuthorityActivationFlow />} />
                      <Route path="/reviews" element={<Reviews />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/thank-you" element={<ThankYou />} />
                      <Route path="/business-card" element={<BusinessCard />} />
                      <Route path="/advisor-stewardship-access" element={<AdvisorStewardshipAccess />} />
                      {/* Emergency Access Routes */}
                      <Route path="/emergency-approval/owner/:requestId" element={<EmergencyApproval />} />
                      <Route path="/emergency-approval/family/:requestId" element={<FamilyApproval />} />
                      <Route path="/emergency-approvals" element={<EmergencyDualApproval />} />
                      <Route path="/legal-document-review" element={<Suspense fallback={<LoadingSpinner type="page" />}><LegalDocumentReview /></Suspense>} />
                      {/* Partner Portal Routes */}
                      <Route path="/partner/login" element={<PartnerLogin />} />
                      <Route path="/partner/invite/:token" element={<PartnerInvite />} />
                      <Route path="/partner" element={<PartnerProtectedRoute><PartnerDashboard /></PartnerProtectedRoute>} />
                      <Route path="/partner/families" element={<PartnerProtectedRoute><PartnerFamilyList /></PartnerProtectedRoute>} />
                      <Route path="/partner/families/create" element={<PartnerProtectedRoute><PartnerCreateFamily /></PartnerProtectedRoute>} />
                      <Route path="/partner/families/:id" element={<PartnerProtectedRoute><PartnerManageFamily /></PartnerProtectedRoute>} />
                      <Route path="/partner/emergency-vault/:memberId" element={<PartnerProtectedRoute><PartnerEmergencyVault /></PartnerProtectedRoute>} />
                      <Route path="/partner/offline-continuity/:familyId" element={<PartnerProtectedRoute><OfflineContinuity /></PartnerProtectedRoute>} />
                      <Route path="/offline-continuity/:familyId" element={<OfflineContinuityFamilyView />} />
                      <Route path="/partner/branding" element={<PartnerProtectedRoute><PartnerBranding /></PartnerProtectedRoute>} />
                      <Route path="/partner/communications" element={<PartnerProtectedRoute><Suspense fallback={<LoadingSpinner type="page" />}><PartnerCommunications /></Suspense></PartnerProtectedRoute>} />
                      <Route path="/partner/reports" element={<PartnerProtectedRoute><Suspense fallback={<LoadingSpinner type="page" />}><PartnerReports /></Suspense></PartnerProtectedRoute>} />
                      <Route path="/partner/succession" element={<PartnerProtectedRoute><Suspense fallback={<LoadingSpinner type="page" />}><PartnerSuccessionTab /></Suspense></PartnerProtectedRoute>} />
                      <Route path="/partner/account" element={<PartnerProtectedRoute><PartnerAccountSettings /></PartnerProtectedRoute>} />

                      {/* Continuity Activation Mandate - principal first-login flow for partner-created families */}
                      <Route path="/continuity-activation" element={<ContinuityActivationMandate />} />

                      {/* Family Office Routes */}
                      <Route path="/fo/dashboard" element={<FOProtectedRoute><FODashboard /></FOProtectedRoute>} />
                      <Route path="/fo" element={<Navigate to="/fo/dashboard" replace />} />
                      <Route path="/fo/identity-map" element={<FOProtectedRoute><FOIdentityMap /></FOProtectedRoute>} />
                      <Route path="/fo/risk-graph" element={<FOProtectedRoute><FORiskGraph /></FOProtectedRoute>} />
                      <Route path="/fo/cross-family" element={<FOProtectedRoute><FOCrossFamily /></FOProtectedRoute>} />
                      <Route path="/fo/emergency-governance" element={<FOProtectedRoute><FOEmergencyGovernance /></FOProtectedRoute>} />
                      <Route path="/fo/emergency-approvals" element={<FOProtectedRoute><Suspense fallback={<LoadingSpinner type="page" />}><FOEmergencyApprovals /></Suspense></FOProtectedRoute>} />
                      <Route path="/fo/succession" element={<FOProtectedRoute><Suspense fallback={<LoadingSpinner type="page" />}><FOSuccession /></Suspense></FOProtectedRoute>} />
                      <Route path="/fo/communications" element={<FOProtectedRoute><Suspense fallback={<LoadingSpinner type="page" />}><FOCommunications /></Suspense></FOProtectedRoute>} />
                      <Route path="/fo/reports" element={<FOProtectedRoute><Suspense fallback={<LoadingSpinner type="page" />}><FOReports /></Suspense></FOProtectedRoute>} />
                      <Route path="/fo/ledger" element={<FOProtectedRoute><FOLedger /></FOProtectedRoute>} />
                      <Route path="/fo/families" element={<FOProtectedRoute><FOFamilies /></FOProtectedRoute>} />
                      <Route path="/fo/families/create" element={<FOProtectedRoute><FOCreateFamily /></FOProtectedRoute>} />
                      <Route path="/fo/families/:familyId" element={<FOProtectedRoute><FOFamilyDetail /></FOProtectedRoute>} />
                      <Route path="/fo/continuity-dependencies" element={<FOProtectedRoute><FOContinuityDependencies /></FOProtectedRoute>} />
                      <Route path="/fo/review-signals" element={<FOProtectedRoute><FOReviewSignals /></FOProtectedRoute>} />
                      <Route path="/fo/families/:familyId/stability" element={<FOProtectedRoute><FOStabilityLens /></FOProtectedRoute>} />
                      <Route path="/fo/families/:familyId/seismic-drift" element={<FOProtectedRoute><FOSeismicDrift /></FOProtectedRoute>} />
                      <Route path="/fo/families/:familyId/succession" element={<FOProtectedRoute><Suspense fallback={<LoadingSpinner type="page" />}><FOFamilySuccessionTab /></Suspense></FOProtectedRoute>} />
                      <Route path="/fo/families/:familyId/stability-assessment/:assessmentId/questionnaire" element={<FOProtectedRoute><FOStabilityQuestionnaire /></FOProtectedRoute>} />
                      <Route path="/fo/offline-continuity/:familyId" element={<FOProtectedRoute><OfflineContinuity /></FOProtectedRoute>} />
                      <Route path="/fo/emergency-vault/:memberId" element={<FOProtectedRoute><FOEmergencyVault /></FOProtectedRoute>} />
                      <Route path="/fo/documents/upload" element={<FOProtectedRoute><FODocumentUpload /></FOProtectedRoute>} />
                      <Route path="/fo/account" element={<FOProtectedRoute><FOAccountManagement /></FOProtectedRoute>} />

                      <Route path="/admin/partners" element={<ProtectedAdminPartnerManagement />} />
                      <Route path="/admin/access-requests" element={<ProtectedAdminAccessRequests />} />
                      <Route path="/white-label" element={<WhiteLabel />} />
                      <Route path="/investor-pack" element={<InvestorTeaser />} />
                      <Route path="/advisor-pack" element={<AdvisorPack />} />
                      <Route path="/advisor-pack/view" element={<AdvisorPackView />} />
                      <Route path="/for-advisors" element={<ForAdvisors />} />
                      <Route path="/fo-continuity-infrastructure" element={<FOContinuityInfrastructure />} />
                      <Route path="/legal-continuity-infrastructure" element={<LegalContinuityInfrastructure />} />
                      <Route path="/book-continuity-session" element={<BookSession />} />
                      <Route path="/commissioning/:clientSlug" element={<Commissioning />} />
                      <Route path="/family-office-pack" element={<FamilyOfficePack />} />
                      <Route path="/engagement-outline-pack" element={<EngagementOutlinePack />} />
                      <Route path="/private-editions-pack" element={<PrivateEditionPack />} />
                      <Route path="/patrons-pack" element={<PatronsPack />} />
                      <Route path="/security-dashboard" element={<SecurityDashboard />} />
                      <Route path="/private-editions" element={<PrivateEditions />} />
                      <Route path="/institutional" element={<InstitutionalSolutions />} />
                      <Route path="/zero-knowledge-test" element={<ProtectedZeroKnowledgeTest />} />
                      <Route path="/blog/zero-knowledge-architecture" element={<ZeroKnowledgeArchitecture />} />
                      <Route path="/blog/digital-inheritance-security" element={<DigitalInheritanceSecurity />} />
                      <Route path="/blog/family-data-protection" element={<FamilyDataProtection />} />
                      <Route path="/blog/encryption-best-practices" element={<EncryptionBestPractices />} />
                      <Route path="/family-resources" element={<Resources />} />

                      {/* Brochures - Protected by passcode gate */}
                      <Route path="/brochures" element={
                        <Suspense fallback={<div>Loading...</div>}>
                          <BrochurePasscodeGate>
                            <BrochuresIndex />
                          </BrochurePasscodeGate>
                        </Suspense>
                      } />
                      <Route path="/brochures/c01" element={
                        <Suspense fallback={<div>Loading...</div>}>
                          <BrochurePasscodeGate>
                            <C01_Breakage />
                          </BrochurePasscodeGate>
                        </Suspense>
                      } />
                      <Route path="/brochures/c02" element={
                        <Suspense fallback={<div>Loading...</div>}>
                          <BrochurePasscodeGate>
                            <C02_Layer />
                          </BrochurePasscodeGate>
                        </Suspense>
                      } />
                      <Route path="/brochures/c03" element={
                        <Suspense fallback={<div>Loading...</div>}>
                          <BrochurePasscodeGate>
                            <C03_Architecture />
                          </BrochurePasscodeGate>
                        </Suspense>
                      } />
                      <Route path="/brochures/c04" element={
                        <Suspense fallback={<div>Loading...</div>}>
                          <BrochurePasscodeGate>
                            <C04_Advisor />
                          </BrochurePasscodeGate>
                        </Suspense>
                      } />
                      <Route path="/brochures/c05" element={
                        <Suspense fallback={<div>Loading...</div>}>
                          <BrochurePasscodeGate>
                            <C05_FamilyOffice />
                          </BrochurePasscodeGate>
                        </Suspense>
                      } />
                      <Route path="/brochures/c06" element={
                        <Suspense fallback={<div>Loading...</div>}>
                          <BrochurePasscodeGate>
                            <C06_Boundary />
                          </BrochurePasscodeGate>
                        </Suspense>
                      } />
                      <Route path="/brochures/c07" element={
                        <Suspense fallback={<div>Loading...</div>}>
                          <BrochurePasscodeGate>
                            <C07_Commissioning />
                          </BrochurePasscodeGate>
                        </Suspense>
                      } />
                      <Route path="/brochures/c07-fo" element={
                        <Suspense fallback={<div>Loading...</div>}>
                          <BrochurePasscodeGate>
                            <C07_FO />
                          </BrochurePasscodeGate>
                        </Suspense>
                      } />
                      <Route path="/brochures/c07-adv" element={
                        <Suspense fallback={<div>Loading...</div>}>
                          <BrochurePasscodeGate>
                            <C07_ADV />
                          </BrochurePasscodeGate>
                        </Suspense>
                      } />
                      <Route path="/brochures/c08" element={
                        <Suspense fallback={<div>Loading...</div>}>
                          <BrochurePasscodeGate>
                            <C08_Invitation />
                          </BrochurePasscodeGate>
                        </Suspense>
                      } />
                      <Route path="/brochures/c09-founding" element={
                        <Suspense fallback={<div>Loading...</div>}>
                          <BrochurePasscodeGate>
                            <C09_FoundingPartner />
                          </BrochurePasscodeGate>
                        </Suspense>
                      } />
                      <Route path="/brochures/c10-cobranded" element={
                        <Suspense fallback={<div>Loading...</div>}>
                          <BrochurePasscodeGate>
                            <C10_CoBranded />
                          </BrochurePasscodeGate>
                        </Suspense>
                      } />
                      <Route path="/brochures/c11-whitelabel" element={
                        <Suspense fallback={<div>Loading...</div>}>
                          <BrochurePasscodeGate>
                            <C11_WhiteLabel />
                          </BrochurePasscodeGate>
                        </Suspense>
                      } />
                      <Route path="/brochures/c12-fo" element={
                        <Suspense fallback={<div>Loading...</div>}>
                          <BrochurePasscodeGate>
                            <C12_FOEdition />
                          </BrochurePasscodeGate>
                        </Suspense>
                      } />
                      <Route path="/brochures/c13-private" element={
                        <Suspense fallback={<div>Loading...</div>}>
                          <BrochurePasscodeGate>
                            <C13_PrivateEdition />
                          </BrochurePasscodeGate>
                        </Suspense>
                      } />

                      <Route path="/login" element={<Login />} />
                      <Route path="/signup" element={<SignUp />} />
                      <Route path="/access-request-confirmation" element={<AccessRequestConfirmation />} />
                      <Route path="/invite-signup" element={<InviteSignup />} />
                      <Route path="/join" element={<DirectSignup />} />
                      <Route path="/onboarding" element={<ProtectedOnboarding />} />
                      <Route path="/forgot" element={<ForgotPassword />} />
                      <Route path="/verify-email" element={<VerifyEmail />} />
                      <Route path="/forgot-passcode" element={<ForgotPasscode />} />
                      <Route path="/reset-passcode" element={<ResetPasscode />} />
                      <Route path="/reset-success" element={<ResetSuccess />} />
                      <Route path="/email-change-approved" element={<EmailChangeApproved />} />
                      <Route path="/email-change-rejected" element={<EmailChangeRejected />} />
                      <Route path="/cp-acknowledged" element={<CpAcknowledged />} />
                      <Route path="/upload-memory/:memberId" element={<SubscriptionProtectedUploadMemory />} />
                      <Route path="/gallery/:memberId" element={<SubscriptionProtectedGallery />} />
                      <Route path="/family-vault/:memberId" element={<SubscriptionProtectedFamilyVault />} />
                      <Route path="/continuity-timeline/:memberId" element={<SubscriptionProtectedContinuityTimeline />} />
                      <Route path="/family-stories/:memberId" element={<SubscriptionProtectedFamilyStories />} />
                      <Route path="/family-stories/:memberId/:storyId" element={<SubscriptionProtectedFamilyStories />} />
                      <Route path="/dashboard/:memberId/zero-knowledge-test" element={<ProtectedZeroKnowledgeTest />} />
                      <Route path="/dashboard/:memberId/emergency-access" element={<ProtectedEmergencyAccessPage />} />
                      <Route path="/how" element={<HowItWorks />} />
                      <Route path="/case-studies" element={<LegacyCaseStudies />} />
                      {/* <Route path="/privacy-folders/:memberId" element={<SubscriptionProtectedPrivacyFolder />} /> */}
                      <Route path="/privacy-folders/:memberId" element={<SubscriptionProtectedEnhancedPrivacyFolders />} />

                      {/* Sovereign Aegis - Multi-Signature Recovery */}
                      <Route path="/aegis/:memberId" element={<SubscriptionProtectedSovereignAegis />} />
                      {/* Succession Planning - Governance-grade role-based succession */}
                      <Route path="/succession-planning/:memberId" element={<SubscriptionProtectedSuccessionPlanning />} />
                      {/* Recovery Flow - PUBLIC routes (no auth - for locked out users) */}
                      <Route path="/recovery/initiate" element={<RecoveryInitiate />} />
                      <Route path="/recovery/status/:requestToken" element={<RecoveryStatus />} />
                      <Route path="/recovery/approve/:approvalToken" element={<GuardianApproval />} />
                      <Route path="/guardian/accept/:token" element={<GuardianAccept />} />
                      <Route path="/recovery/complete/:requestToken" element={<RecoveryComplete />} />
                      <Route path="/member-area" element={<SubscriptionProtectedMemberLogin />} />
                      <Route path="/member-selection" element={<SubscriptionProtectedMemberSelection />} />
                      <Route path="/family-continuity-structure" element={<SubscriptionProtectedAccountManagement />} />
                      <Route path="/add-member" element={<SubscriptionProtectedAddMember />} />
                      <Route path="/passcode-entry" element={<SubscriptionProtectedPasscodeEntry />} />
                      <Route path="/legal" element={<Legal />} />
                      <Route path="/create-family" element={<SubscriptionProtectedCreateFamily />} />
                      <Route path="/choose-plan" element={<WhoWeServe />} />
                      {/* <Route path="/eterna-vaults-founding-circle" element={<LandingPage />} /> */}
                      <Route path="/audit-logs" element={<ProtectedAuditLogs />} />
                      <Route path="/audit-trail" element={<ProtectedAuditTrail />} />
                      <Route path="/pending-approvals" element={<ProtectedPendingApprovalsPage />} />
                      <Route path="/security-demo" element={<SecurityDemo />} />
                      <Route path="/demo" element={<ProtectedDemo />} />
                      <Route path="/mfa-test" element={<MFATest />} />
                      <Route path="/encryption-test" element={<EncryptionTest />} />
                      <Route path="/test-emergency-access" element={<TestEmergencyAccess />} />
                      <Route path="/emergency-access-demo" element={<EmergencyAccessDemo />} />
                      <Route path="/security" element={<Security />} />
                      <Route path="/security-transparency" element={<SecurityTransparency />} />
                      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                      <Route path="/terms-of-service" element={<TermsOfService />} />
                      <Route path="/family-legacy-guide" element={<FamilyLegacyGuide />} />
                      <Route path="/why-eternavaults" element={<WhyEternaVaults />} />
                      <Route path="/pillars-of-continuity" element={<PillarsOfContinuity />} />
                      <Route path="/continuity-architecture" element={<ContinuityArchitecture />} />
                      <Route path="/continuity-architecture-stack" element={<ContinuityArchitectureStack />} />
                      <Route path="/authority-decision-architecture" element={<AuthorityAndDecisionArchitecture />} />
                      <Route path="/glossary" element={<Glossary />} />
                      <Route path="/temporal-continuity" element={<TemporalContinuity />} />
                      <Route path="/continuity-infrastructure" element={<Suspense fallback={<LoadingSpinner type="page" />}><ContinuityInfrastructure /></Suspense>} />
                      <Route path="/continuity-seismic-map" element={<ContinuitySeismicMap />} />
                      <Route path="/governance-vs-storage" element={<Suspense fallback={<LoadingSpinner type="page" />}><GovernanceVsStorage /></Suspense>} />
                      <Route path="/what-we-are-known-for" element={<Suspense fallback={<LoadingSpinner type="page" />}><WhatWeAreKnownFor /></Suspense>} />
                      <Route path="/the-problem" element={<Suspense fallback={<LoadingSpinner type="page" />}><TheProblem /></Suspense>} />
                      <Route path="/continuity-infrastructure-review-2025" element={<Suspense fallback={<LoadingSpinner type="page" />}><ContinuityInfrastructureReview /></Suspense>} />
                      <Route path="/temporal-continuity-execution" element={<Suspense fallback={<LoadingSpinner type="page" />}><TemporalContinuityExecution /></Suspense>} />
                      <Route path="/succession-aware-architecture" element={<Suspense fallback={<LoadingSpinner type="page" />}><SuccessionAwareArchitecture /></Suspense>} />
                      <Route path="/private-digital-heirlooms" element={<Suspense fallback={<LoadingSpinner type="page" />}><PrivateDigitalHeirlooms /></Suspense>} />
                      <Route path="/continuity-os" element={<ContinuityOS />} />
                      <Route path="/zero-knowledge-governance" element={<Suspense fallback={<LoadingSpinner type="page" />}><ZeroKnowledgeGovernance /></Suspense>} />
                      <Route path="/continuity-domains" element={<Suspense fallback={<LoadingSpinner type="page" />}><ContinuityDomains /></Suspense>} />
                      {/* SEO Article Landing Pages */}
                      <Route path="/what-is-continuity-infrastructure-eternavaults" element={<Suspense fallback={<LoadingSpinner type="page" />}><ContinuityInfrastructureArticle /></Suspense>} />
                      <Route path="/temporal-continuity-eternavaults" element={<Suspense fallback={<LoadingSpinner type="page" />}><TemporalContinuityEternaVaults /></Suspense>} />
                      <Route path="/firevault-cold-storage-eternavaults" element={<Suspense fallback={<LoadingSpinner type="page" />}><FirevaultColdStorageEternaVaults /></Suspense>} />
                      <Route path="/succession-aware-architecture-eternavaults" element={<Suspense fallback={<LoadingSpinner type="page" />}><SuccessionAwareArchitectureEternaVaults /></Suspense>} />
                      <Route path="/process" element={<ProcessPage />} />
                      <Route path="/explore-features" element={<ExploreFeatures />} />
                      <Route path="/competitor-comparison" element={<CompetitorComparison />} />
                      <Route path="/monitoring" element={<Monitoring />} />
                      <Route path="/ux-demo" element={<UXDemo />} />
                      <Route path="/message-center" element={<ProtectedMessageCenter />} />
                      <Route path="/emergency-access/:id?" element={<ProtectedEmergencyAccess />} />
                      <Route path="/emergency-dashboard" element={<EmergencyDashboard />} />
                      <Route path="/firevault/:memberId" element={<ProtectedFirevault />} />

                      {/* Landing Pages */}
                      <Route path="/digital-vault" element={<DigitalVault />} />
                      <Route path="/digital-family-vault" element={<DigitalFamilyVault />} />
                      <Route path="/legacy-planning" element={<LegacyPlanning />} />
                      <Route path="/estate-document-storage" element={<EstateDocumentStorage />} />
                      {/* Location Landing Pages */}
                      <Route path="/digital-vault-sydney" element={<DigitalVaultSydney />} />
                      <Route path="/digital-vault-melbourne" element={<DigitalVaultMelbourne />} />
                      <Route path="/digital-vault-brisbane" element={<DigitalVaultBrisbane />} />
                      <Route path="/family-office-digital-legacy" element={<FamilyOfficeDigitalLegacy />} />
                      <Route path="/institutional-digital-vault" element={<InstitutionalDigitalVault />} />
                      <Route path="/advisor-access" element={<AdvisorAccess />} />

                      {/* How-to Guides */}
                      <Route path="/how-to-guides" element={<HowToGuides />} />

                      {/* Blog */}
                      <Route path="/blog" element={<Blog />} />
                      <Route path="/blog/:slug" element={<BlogPost />} />
                      <Route path="/blog/category/:category" element={<Blog />} />
                      <Route path="/blog/what-is-digital-vault" element={<WhatIsDigitalVaultBlog />} />
                      <Route path="/blog/digital-vault-security-2025" element={<DigitalVaultSecurity2024 />} />
                      <Route path="/blog/digital-vault-access-levels-permissions" element={<DigitalVaultAccessLevels />} />
                      <Route path="/blog/digital-legacy-planning-complete-guide-2025" element={<DigitalLegacyPlanning2025 />} />
                      <Route path="/blog/digital-estate-planning-gap-2025" element={<DigitalEstatePlanningGap2025 />} />
                      <Route path="/blog/digital-continuity-infrastructure" element={<DigitalContinuityInfrastructure />} />
                      <Route path="/blog/digital-continuity-for-family-offices" element={<DigitalContinuityForFamilyOffices />} />
                      <Route path="/best-digital-vault-2025" element={<BestDigitalVault2025 />} />
                      <Route path="/what-is-digital-vault" element={<WhatIsDigitalVault />} />
                      <Route path="/digital-vault-reviews" element={<DigitalVaultReviews />} />
                      {/* <Route path="/blog/eternavaults-vs-presidio-2025" element={<EternaVaultsVsPresidio2025 />} /> */}
                      <Route path="/digital-vault" element={<DigitalVaultPage />} />
                      <Route path="/blog/why-traditional-cloud-storage-fails-family-memories" element={<WhyTraditionalCloudStorageFails />} />
                      <Route path="/blog/emergency-access-protecting-digital-assets" element={<EmergencyAccessProtectingDigitalAssets />} />
                      <Route path="/blog/the-hidden-costs-of-losing-family-digital-memories" element={<TheHiddenCostsOfLosingFamilyDigitalMemories />} />
                      <Route path="/blog/compare-digital-legacy-platforms" element={<CompareDigitalLegacyPlatforms />} />
                      <Route path="/blog/family-digital-vault-access-levels-permissions-guide-2025" element={<FamilyDigitalVaultAccessLevels />} />
                      <Route path="/blog/preserving-family-stories-emotional-digital-legacy" element={<PreservingFamilyStoriesDigitalLegacy />} />
                      <Route path="/blog/the-architecture-of-legacy" element={<TheArchitectureOfLegacy />} />
                      <Route path="/blog/the-new-heirloom-preserving-digital-assets-across-generations" element={<TheNewHeirloom />} />

                      {/* Legacy Journal & Archive */}
                      <Route path="/journal" element={<LegacyJournalHome />} />
                      <Route path="/journal/:slug" element={<LegacyArticle />} />
                      <Route path="/archive" element={<ArchiveHome />} />
                      <Route path="/archive/:slug" element={<ArchiveEntry />} />

                      {/* Founder Musings */}
                      <Route path="/founder-musings" element={<FounderMusings />} />
                      <Route path="/founder-musings/:slug" element={<FounderMusings />} />
                      <Route path="/document-signature" element={<DocumentSignaturePage />} />
                      <Route path="/addon-features" element={<AddonFeatures />} />
                      <Route path="/maintenance" element={<Maintenance />} />

                      {/* Private Style Guide - Internal Use Only */}
                      <Route path="/style-guide" element={<ProtectedStyleGuide />} />

                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </MaintenanceWrapper>
                </Suspense>
                <ToastContainer position="top-center" style={{ zIndex: 99999 }} />
                {/* PWA Install Prompt disabled - user feedback indicated it was obstructive */}
                {/* <PWAInstallPrompt /> */}
                <DemoControlPanel />
                <ConditionalFooter />
              </HelmetProvider>
            </NotificationProvider>
          </WhiteLabelProvider>
        </PartnerAuth0Provider>
      </GlobalErrorBoundary>
    </QueryClientProvider>
  );
}

// Main App with Cookie Consent
const AppWithCookieConsent = () => {
  return (
    <CookieConsentProvider>
      <App />
      <CookieConsentBanner />
      <CookieManagementModal />
      {/* <CookieDevPanel /> */}
    </CookieConsentProvider>
  );
};

export default AppWithCookieConsent;
